"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { XIcon } from "@phosphor-icons/react";

import {
  CONSENT_OPEN_SETTINGS_EVENT,
  type ConsentCategories,
  openConsentSettings,
  readConsent,
  writeConsent,
} from "@/lib/public/consent";

const necessaryCategory = {
  title: "Zorunlu çerezler",
  description:
    "Oturum güvenliği ve bu tercihlerinizi saklamak için gereklidir. Bunlar olmadan site çalışmaz.",
};

const optionalCategories: Array<{
  key: keyof ConsentCategories;
  title: string;
  description: string;
}> = [
  {
    key: "analytics",
    title: "Analitik çerezler",
    description:
      "Google Analytics 4 ve Tag Manager gibi araçlarla ziyaretçi davranışını anonim olarak ölçmemizi sağlar.",
  },
  {
    key: "marketing",
    title: "Pazarlama çerezleri",
    description:
      "Meta Pixel gibi araçlarla ilgi alanınıza yönelik kampanya ve reklam ölçümlemesi yapılır.",
  },
];

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selection, setSelection] = useState<ConsentCategories>({
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = readConsent();
    if (!consent) setShowBanner(true);
    else
      setSelection({
        analytics: consent.analytics,
        marketing: consent.marketing,
      });
    const openSettings = () => setShowSettings(true);
    window.addEventListener(CONSENT_OPEN_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(CONSENT_OPEN_SETTINGS_EVENT, openSettings);
  }, []);

  useEffect(() => {
    if (!showSettings) return;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setShowSettings(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showSettings]);

  const save = (value: ConsentCategories) => {
    writeConsent(value);
    setSelection(value);
    setShowBanner(false);
    setShowSettings(false);
  };

  return (
    <>
      {showBanner ? (
        <div className="cookie-consent" role="region" aria-label="Çerez tercihi">
          <div className="cookie-consent__card">
            <div className="cookie-consent__text">
              <h2>Çerez Tercihleriniz</h2>
              <p>
                Sitemizde oturumunuzun çalışması için zorunlu çerezleri kullanıyoruz.
                Deneyiminizi geliştirmek isteyen analitik ve pazarlama çerezlerini ancak
                onayınızla devreye alıyoruz. Ayrıntılar için{" "}
                <Link href="/cerez-politikasi">Çerez Politikası</Link> sayfamızı
                inceleyebilirsiniz.
              </p>
            </div>
            <div className="cookie-consent__actions">
              <button
                type="button"
                className="public-button public-button--accent"
                onClick={() => save({ analytics: true, marketing: true })}
              >
                Kabul Et
              </button>
              <button
                type="button"
                className="cookie-consent__ghost"
                onClick={() => setShowSettings(true)}
              >
                Ayarlar
              </button>
              <button
                type="button"
                className="cookie-consent__ghost"
                onClick={() => save({ analytics: false, marketing: false })}
              >
                Reddet
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showSettings ? (
        <div
          className="cookie-settings"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-settings-title"
          onClick={(event) => {
            if (event.target === event.currentTarget) setShowSettings(false);
          }}
        >
          <div className="cookie-settings__card">
            <header className="cookie-settings__head">
              <div>
                <p className="public-eyebrow">Gizlilik</p>
                <h2 id="cookie-settings-title">Çerez Ayarları</h2>
              </div>
              <button
                type="button"
                className="cookie-settings__close"
                aria-label="Kapat"
                onClick={() => setShowSettings(false)}
              >
                <XIcon size={18} aria-hidden="true" />
              </button>
            </header>
            <p className="cookie-settings__intro">
              Hangi kategorideki çerezlere izin verdiğinizi seçin. Seçiminizi
              dilediğiniz zaman sitemizin alt bilgisindeki Çerez Ayarları bağlantısından
              değiştirebilirsiniz.
            </p>
            <ul className="cookie-settings__list">
              <li>
                <label className="cookie-switch-row">
                  <span className="cookie-switch-row__text">
                    <strong>{necessaryCategory.title}</strong>
                    <small>{necessaryCategory.description}</small>
                  </span>
                  <input type="checkbox" className="cookie-switch" checked disabled />
                </label>
              </li>
              {optionalCategories.map((category) => (
                <li key={category.key}>
                  <label className="cookie-switch-row">
                    <span className="cookie-switch-row__text">
                      <strong>{category.title}</strong>
                      <small>{category.description}</small>
                    </span>
                    <input
                      type="checkbox"
                      className="cookie-switch"
                      checked={selection[category.key]}
                      onChange={(event) =>
                        setSelection((current) => ({
                          ...current,
                          [category.key]: event.target.checked,
                        }))
                      }
                    />
                  </label>
                </li>
              ))}
            </ul>
            <div className="cookie-settings__actions">
              <button
                type="button"
                className="public-button public-button--accent"
                onClick={() => save({ analytics: true, marketing: true })}
              >
                Tümünü Kabul Et
              </button>
              <button
                type="button"
                className="public-button"
                onClick={() => save(selection)}
              >
                Seçimleri Kaydet
              </button>
              <button
                type="button"
                className="cookie-consent__ghost"
                onClick={() => save({ analytics: false, marketing: false })}
              >
                Tümünü Reddet
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

/** Footer / policy-page trigger to reopen preferences after a decision. */
export function CookieSettingsButton({ className }: { className?: string }) {
  return (
    <button type="button" className={className} onClick={() => openConsentSettings()}>
      Çerez Ayarları
    </button>
  );
}
